import React, { useRef, useState, useEffect, useMemo } from "react";
import СreateDOMPurify from "dompurify";
import { debounce } from "throttle-debounce";
import classnames from "classnames/bind";
import { observer } from "mobx-react";

import { Scrollbars } from "react-custom-scrollbars";

import Preloader from "@components/preloader";
import Skeleton from "@components/skeleton";
import Icon from "@components/icon";

import PopupLink from "../PopupLink";
import FirmAvatar from "./components/FirmAvatar";
import Stars from "../Stars";
import SearchRestrictionMessage from "./components/SeachRestrictionMessage";

import FirmApi from "../../api/FirmApi";

import { sendNavigationMetric } from "../../utils/sendMetric";
import getScrollDimensions from "./getScrollDimensions";
import { useStore } from "../../connect";

import styles from "./Search.scss";

import strings from "../../locales/strings";

const cx = classnames.bind(styles);

const Search = () => {
  const { baseUrl, search, adaptive, profile } = useStore();

  const firmApi = useRef(new FirmApi());
  const scrollDimensions = useRef(getScrollDimensions());

  const [datalist, setDatalist] = useState([]);
  const [labels, setLabels] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isActive, setIsActive] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const [firmIcons, setFirmIcons] = useState({});
  const [isError, setIsError] = useState(false);

  const input = useRef(null);
  const block = useRef(null);

  const getFirmIcons = (ids) => {
    if (!ids.length) return;

    firmApi.current
      .getFirmIcons(ids)
      .then((response) => {
        setFirmIcons(response.data);
      })
      .catch((error) => {
        console.error(error);
        setFirmIcons({});
      });
  };

  const fetchSearchResults = (keywords) => {
    firmApi.current
      .getFirmSearchResults(keywords)
      .then((response) => {
        if (
          (response.status === 200 || response.status === 201) &&
          response.data
        ) {
          getFirmIcons(response.data.data.map((firmInfo) => firmInfo.alias_id));

          setDatalist(response.data.data);
          setLabels(response.data.labels);
          setIsLoading(false);
          setNotFound(true);
        } else {
          setDatalist([]);
          setIsLoading(false);
          setNotFound(false);
        }
      })
      .catch((error) => {
        console.log("Error in quick search", error);
        setDatalist([]);
        setIsLoading(false);
        setIsError(true);
      });
  };

  const debounceSearchFirms = useMemo(
    () =>
      debounce(500, (keywords) => {
        fetchSearchResults(keywords);
      }),
    []
  );

  const getFirmsSearchResults = (value) => {
    if (value && value.trim().length >= 1) {
      setIsLoading(true);
      setIsError(false);

      const requestPayload = {
        keywords: value.trim(),
      };

      debounceSearchFirms(requestPayload);
    } else if (!value || value.trim().length === 0) {
      setIsLoading(false);
      setDatalist([]);
      setNotFound(false);
      setIsError(false);
      setFirmIcons({});
    }
  };

  const openSearchPopup = (event) => {
    event.preventDefault();
    event.stopPropagation();

    sendNavigationMetric("EXTENSION_SEARCH");

    search.openExtendedSearchPopup();

    search.hideSearch();
  };

  const handleChange = (event) => {
    event.preventDefault();
    event.stopPropagation();
    getFirmsSearchResults(event.target.value);
    setSearchValue(event.target.value);
  };

  const handleClearClick = (event) => {
    event.preventDefault();
    event.stopPropagation();

    search.hideSearch(event);
  };

  const handleOutsideClick = (event) => {
    if (block.current && block.current.contains(event.target)) {
      setIsActive(true);
    } else {
      setIsActive(false);

      search.hideSearch();
    }
  };

  useEffect(() => {
    document.addEventListener("click", handleOutsideClick);
    input.current.click();

    return () => {
      document.removeEventListener("click", handleOutsideClick);
    };
  }, []);

  const highlighter = (label, value, key) => {
    const prepareKey = key.trim().replace(/\W/g, "\\$&");
    const regex = new RegExp(`(${prepareKey})`, "gi");
    const matchedValue = value.replace(
      regex,
      `<span class=${cx("highlighter")}>$1</span>`
    );

    if (label) {
      return `${label}&nbsp;${matchedValue}`;
    }

    return `<span>${matchedValue}</span>`;
  };

  const renderFirmResult = (resultObject) => {
    const firmIcon = firmIcons[resultObject.alias_id];

    let matchedLabels = resultObject.matches_fields.split(", ");

    if (matchedLabels[0] === "") {
      matchedLabels = ["name"];
    }

    matchedLabels = matchedLabels.filter((field) => field !== "alias_id");
    matchedLabels.unshift("alias_id");

    const tags = matchedLabels.map((label) => {
      const lastCharIndex = resultObject[label].length - 1;
      resultObject[label] =
        resultObject[label][lastCharIndex] === ","
          ? resultObject[label].slice(0, -1)
          : resultObject[label];

      return highlighter(labels[label], resultObject[label], searchValue);
    });

    const tagsStr = tags.join(", ");

    const firmId = resultObject.alias_id || resultObject.firm_id;
    const isEmptyStars =
      (!firmIcon && !isLoading) || (firmIcon && firmIcon.stars_icon === null);

    return (
      <PopupLink
        className={cx("firm-result-link")}
        href={`https://${baseUrl}/firms/${resultObject.alias_id}/info?utm_source=header&utm_campaign=new_header`}
        key={firmId}
        name={`search-card-${firmId}`}
        params="top=150,left=200,width=950,height=970,resizable=1,scrollbars=1,menubar=0"
      >
        <div
          className={cx("firm-result")}
          data-alias-id={resultObject.alias_id}
          key={firmId}
        >
          <FirmAvatar firmId={firmId} baseUrl={baseUrl} />

          <div className={cx("firm-text")}>
            <a
              className={cx("firm-name", isEmptyStars && "empty-stars")}
              data-name="firm-name"
              // eslint-disable-next-line react/no-danger
              dangerouslySetInnerHTML={{
                __html: СreateDOMPurify.sanitize(
                  highlighter(false, resultObject.name, searchValue)
                ),
              }}
            />
            {!firmIcon && isLoading && (
              <Skeleton
                className={cx("stars-skeleton")}
                width={110}
                height={14}
                borderRadius={4}
              />
            )}

            {firmIcon && firmIcon.stars_icon && (
              <Stars
                imageLink={firmIcon.stars_icon.image_link}
                imgClassName={cx("firm-stars-img")}
                className={cx("firm-stars")}
                atiCode={resultObject.alias_id}
                baseUrl={baseUrl}
              />
            )}
            <div
              className={cx("firm-tags")}
              data-name="firm-tags"
              // eslint-disable-next-line react/no-danger
              dangerouslySetInnerHTML={{
                __html: СreateDOMPurify.sanitize(tagsStr),
              }}
            />
          </div>
        </div>
      </PopupLink>
    );
  };

  const renderAutocompleteList = () => {
    if (datalist.length > 0) {
      return (
        <Scrollbars
          autoHeight
          autoHeightMax={360}
          style={{
            borderBottomLeftRadius: "4px",
            borderBottomRightRadius: "4px",
            backgroundColor: "var(--glz-color-neutral-tone-0)",
          }}
          renderView={(viewProps) => (
            <div
              style={{
                ...viewProps.style,
                maxHeight:
                  scrollDimensions.current.vertical !==
                  scrollDimensions.current.horizontal
                    ? viewProps.style.maxHeight + scrollDimensions.horizontal
                    : viewProps.style.maxHeight,
              }}
            />
          )}
        >
          <div
            className={cx("dropdown-wrapper")}
            data-name="firm-search-results"
          >
            {datalist.map((item, index) => renderFirmResult(item, index))}
          </div>
        </Scrollbars>
      );
    }

    if (notFound || isError) {
      return (
        <div className={cx("firm-search-nofound")}>
          {`${
            isError ? strings.somethingWentWrong : strings.cannotFindFirm
          }\u00a0`}
          {`${strings.tryUsing}\u00a0`}
          <button
            className={cx("glz-link")}
            onClick={(event) => openSearchPopup(event)}
          >
            {strings.byExtendedSearch}
          </button>
        </div>
      );
    }

    return null;
  };

  return (
    <div className={cx("quick-search")}>
      <div className={cx("quick-firm-search")} ref={block}>
        <div className={cx("search-icon")}>
          {isLoading ? (
            <Preloader width={32} />
          ) : (
            <Icon
              name="ic_search-people"
              color="var(--glz-color-neutral-tone-4)"
              size="l"
            />
          )}
        </div>
        <input
          type="text"
          className={cx("input")}
          placeholder={
            adaptive.isSmallDesktop || adaptive.isDesktop
              ? strings.search_atiParticipantsSearch
              : strings.search_participantsSearch
          }
          onChange={(ev) => handleChange(ev)}
          value={searchValue}
          ref={input}
          // eslint-disable-next-line jsx-a11y/no-autofocus
          autoFocus
        />
        <button
          className={cx("glz-link", "additional-parameters-link", {
            "hide-additional-parameters-link": searchValue.length > 0,
          })}
          onClick={(event) => openSearchPopup(event)}
        >
          <span className={cx("additional-parameters-link-text")}>
            {strings.search_advancedSearch}
          </span>
          <span className={cx("additional-parameters-link-small-mobile-text")}>
            {strings.search_advanced}
          </span>
        </button>
        <button
          onClick={(event) => handleClearClick(event)}
          className={cx("clear-icon-button")}
        >
          <Icon name="ic_close-out" size="m" />
        </button>
        <div className={cx("results-scroll-holder")}>
          {isActive &&
            searchValue &&
            searchValue.trim().length >= 1 &&
            renderAutocompleteList()}
          <SearchRestrictionMessage
            searchPhrase={searchValue}
            isPaidUser={profile.isPaidUser}
            baseUrl={baseUrl}
          />
        </div>
      </div>
    </div>
  );
};

export default observer(Search);
